// File: src/pages/Privacy.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Link } from 'react-router-dom';
import { IoIosArrowBack } from 'react-icons/io';

import { USER_PRIVACY_POLICY } from '../components/legal/userLegalText';

const SECTION_DEFS = [
  {
    id: 'overview',
    label: 'Overview',
    matches: [],
  },
  {
    id: 'information-collected',
    label: 'Information We Collect',
    matches: ['1. INFORMATION WE COLLECT'],
  },
  {
    id: 'how-we-use',
    label: 'How We Use Information',
    matches: ['2. HOW WE USE INFORMATION'],
  },
  {
    id: 'sms-mobile',
    label: 'SMS & Mobile Information',
    matches: ['2.1 SMS AND MOBILE INFORMATION'],
  },
  {
    id: 'disclosures',
    label: 'How We Disclose Information',
    matches: ['3. HOW WE DISCLOSE INFORMATION'],
  },
  {
    id: 'retention-security',
    label: 'Retention & Security',
    matches: ['4. DATA RETENTION'],
  },
  {
    id: 'rights-choices',
    label: 'Your Rights & Choices',
    matches: ['6. YOUR RIGHTS AND CHOICES'],
  },
  {
    id: 'sale-age',
    label: 'Sale & Age Requirements',
    matches: ['7. NO SALE OF PERSONAL INFORMATION'],
  },
  {
    id: 'changes-contact',
    label: 'Changes & Contact',
    matches: [
      '9. CHANGES TO THIS PRIVACY POLICY',
      '9. CHANGES',
    ],
  },
];

export default function Privacy() {
  const [activeSection, setActiveSection] =
    useState('overview');

  const [showSideNav, setShowSideNav] =
    useState(
      typeof window !== 'undefined'
        ? window.innerWidth >= 900
        : true
    );

  const [compactNav, setCompactNav] = useState(
    typeof window !== 'undefined'
      ? window.innerWidth < 700
      : false
  );

  const policyLines = useMemo(
    () =>
      USER_PRIVACY_POLICY
        .split('\n')
        .map((line) => line.trim()),
    []
  );

  // Only show side-nav sections that
  // actually exist in the current policy.
  const pageSections = useMemo(() => {
    return SECTION_DEFS.filter((section) => {
      if (section.id === 'overview') {
        return true;
      }

      return section.matches.some((match) =>
        policyLines.some((line) =>
          line.startsWith(match)
        )
      );
    });
  }, [policyLines]);

  const getAnchorId = (line) => {
    const section = SECTION_DEFS.find(
      (item) =>
        item.id !== 'overview' &&
        item.matches.some((match) =>
          line.startsWith(match)
        )
    );

    return section?.id || null;
  };

  // -----------------------------------------
  // Responsive left navigation
  // -----------------------------------------
  useEffect(() => {
    const handleResize = () => {
      setShowSideNav(window.innerWidth >= 900);
      setCompactNav(window.innerWidth < 700);
    };

    handleResize();

    window.addEventListener(
      'resize',
      handleResize
    );

    return () => {
      window.removeEventListener(
        'resize',
        handleResize
      );
    };
  }, []);

  // -----------------------------------------
  // Track active Privacy section
  // -----------------------------------------
  useEffect(() => {
    const handleScroll = () => {
      const marker = 145;
      let current = 'overview';

      pageSections.forEach(({ id }) => {
        const el =
          document.getElementById(id);

        if (!el) return;

        if (
          el.getBoundingClientRect().top <=
          marker
        ) {
          current = id;
        }
      });

      setActiveSection(current);
    };

    handleScroll();

    window.addEventListener(
      'scroll',
      handleScroll,
      {
        passive: true,
      }
    );

    return () => {
      window.removeEventListener(
        'scroll',
        handleScroll
      );
    };
  }, [pageSections]);

  const goToSection = (id) => {
    const el =
      document.getElementById(id);

    if (!el) return;

    setActiveSection(id);

    const y =
      window.scrollY +
      el.getBoundingClientRect().top -
      100;

    window.scrollTo({
      top: Math.max(0, y),
      behavior: 'smooth',
    });
  };

  // -----------------------------------------
  // Render Privacy copy with hierarchy
  // -----------------------------------------
  const renderPrivacy = () => {
    const lines =
      USER_PRIVACY_POLICY.split('\n');

    return lines.map(
      (rawLine, index) => {
        const line = rawLine.trim();

        const anchorId =
          getAnchorId(line);

        const isBlank =
          line === '';

        const isInternalTitle =
          line ===
          'TRIBE PRIVACY POLICY (USERS)';

        const isEffectiveDate =
          line.startsWith(
            'Effective Date:'
          );

        const isSubheading =
          /^\d+\.\d+\s+[A-Z]/.test(
            line
          );

        const isMainHeading =
          /^\d+\.\s+[A-Z]/.test(
            line
          );

        const isBullet =
          line.startsWith('- ');

        const isImportantMobileStatement =
          line.startsWith(
            'NO MOBILE INFORMATION'
          );

        if (isBlank) {
          return (
            <div
              key={`blank-${index}`}
              style={{ height: 9 }}
            />
          );
        }

        return (
          <React.Fragment
            key={`${index}-${line}`}
          >
            {anchorId && (
              <span
                id={anchorId}
                style={{
                  display: 'block',
                  scrollMarginTop: 100,
                }}
              />
            )}

            {isInternalTitle ? (
              <div
                style={
                  S.legalDocumentTitle
                }
              >
                {line}
              </div>
            ) : isEffectiveDate ? (
              <div
                style={
                  S.legalEffectiveDate
                }
              >
                {line}
              </div>
            ) : isSubheading ? (
              <div
                style={
                  S.legalSubheading
                }
              >
                {line}
              </div>
            ) : isMainHeading ? (
              <div
                style={
                  S.legalSectionHeading
                }
              >
                {line}
              </div>
            ) : isImportantMobileStatement ? (
              <div
                style={
                  S.legalImportant
                }
              >
                {line}
              </div>
            ) : isBullet ? (
              <div
                style={
                  S.legalBullet
                }
              >
                <span
                  style={S.bulletMark}
                >
                  –
                </span>

                <span>
                  {line.slice(2)}
                </span>
              </div>
            ) : (
              <div
                style={
                  S.legalBodyLine
                }
              >
                {line}
              </div>
            )}
          </React.Fragment>
        );
      }
    );
  };

  const S = {
    wrap: {
      minHeight: '100vh',
      background: '#fff',
      color: '#0B1020',

      fontFamily:
        'Open Sans, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
    },

    page: {
      width: '100%',
      maxWidth: 1180,

      margin: '0 auto',

      padding: '0 28px 64px',

      boxSizing: 'border-box',
    },

    // =================================
    // NAVBAR
    // =================================

    topNav: {
      position: 'sticky',
      top: 0,
      zIndex: 80,

      height: 76,

      display: 'flex',
      alignItems: 'center',
      justifyContent:
        'space-between',

      background: '#fff',
    },

    navBreadcrumbs: {
      display: 'flex',
      alignItems: 'center',

      gap: 7,

      whiteSpace: 'nowrap',

      lineHeight: 1,
    },

    homeLink: {
      display: 'inline-flex',
      alignItems: 'center',

      gap: 2,

      color: '#6b7280',
      textDecoration: 'none',

      fontSize: '0.83rem',
      fontWeight: 600,

      lineHeight: 1,
    },

    backIcon: {
      fontSize: 18,
      marginLeft: -5,
    },

    navSlash: {
      color: '#d1d5db',

      fontSize: '0.78rem',
      fontWeight: 400,

      userSelect: 'none',
    },

    currentLegalLink: {
      color: '#0B1020',

      fontSize: '0.83rem',
      fontWeight: 700,

      lineHeight: 1,
    },

    otherLegalLink: {
      color: '#9ca3af',

      textDecoration: 'none',

      fontSize: '0.83rem',
      fontWeight: 500,

      lineHeight: 1,
    },

    legalHeading: {
      marginLeft: 'auto',

      fontSize: '0.95rem',
      fontWeight: 900,

      lineHeight: 1,

      textTransform: 'uppercase',

      letterSpacing: 0.2,

      color: '#0b0b0b',
    },

    // =================================
    // DOCUMENT LAYOUT
    // =================================

    layout: {
      display: 'grid',

      gridTemplateColumns:
        showSideNav
          ? '180px minmax(0, 860px)'
          : 'minmax(0, 860px)',

      columnGap: 54,

      alignItems: 'start',
    },

    sidePlaceholder: {
      width: 180,
    },

    // =================================
    // FIXED SECTION NAV
    // =================================

    sideNav: {
      position: 'fixed',

      left:
        'max(28px, calc(50vw - 590px))',

      top: 112,

      width: 180,

      zIndex: 20,
    },

    sideItems: {
      display: 'flex',

      flexDirection: 'column',

      alignItems: 'flex-start',

      gap: 11,
    },

    sideItem: {
      appearance: 'none',

      border: 0,

      background: 'transparent',

      padding: 0,
      margin: 0,

      fontFamily: 'inherit',

      fontSize: '0.79rem',

      lineHeight: 1.3,

      textAlign: 'left',

      cursor: 'pointer',

      transition:
        'color 140ms ease, opacity 140ms ease, font-weight 140ms ease',
    },

    // =================================
    // DOCUMENT
    // =================================

    content: {
      minWidth: 0,

      paddingTop: 22,
    },

    h1: {
      margin: 0,
      marginBottom: 8,

      fontSize: '1.85rem',
      fontWeight: 700,

      lineHeight: 1.15,

      letterSpacing:
        '-0.025em',
    },

    small: {
      color: '#8b8f96',

      fontSize: '0.78rem',
    },

    // =================================
    // LEGAL COPY
    // =================================

    legalDocumentTitle: {
      marginTop: 25,
      marginBottom: 2,

      color: '#111',

      fontSize: 13,
      fontWeight: 700,

      lineHeight: 1.5,
    },

    legalEffectiveDate: {
      marginBottom: 14,

      color: '#92969d',

      fontSize: 12,

      lineHeight: 1.5,
    },

    legalSectionHeading: {
      marginTop: 21,
      marginBottom: 5,

      color: '#111',

      fontSize: 13.5,
      fontWeight: 700,

      lineHeight: 1.45,
    },

    legalSubheading: {
      marginTop: 13,
      marginBottom: 4,

      color: '#242424',

      fontSize: 13,
      fontWeight: 700,

      lineHeight: 1.5,
    },

    legalImportant: {
      margin: '8px 0',

      color: '#222',

      fontSize: 12.5,
      fontWeight: 700,

      lineHeight: 1.55,
    },

    legalBodyLine: {
      color: '#414141',

      fontSize: 13,
      fontWeight: 400,

      lineHeight: 1.58,
    },

    legalBullet: {
      display: 'flex',

      alignItems: 'flex-start',

      gap: 7,

      paddingLeft: 5,

      color: '#414141',

      fontSize: 13,

      lineHeight: 1.55,
    },

    bulletMark: {
      color: '#a8abb0',

      flexShrink: 0,
    },

    // Gives short final sections enough room
    // to scroll up to the normal active marker.
    bottomScrollSpace: {
      height: '55vh',
      minHeight: 360,
    },
  };

  return (
    <main style={S.wrap}>
      <div style={S.page}>

        {/* =================================
            NAVBAR
        ================================= */}
        <header style={S.topNav}>
          <nav
            style={
              S.navBreadcrumbs
            }
            aria-label="Legal pages"
          >
            <Link
              to="/"
              style={S.homeLink}
            >
              <IoIosArrowBack
                style={S.backIcon}
              />

              Home
            </Link>

            {!compactNav && (
              <>
                <span
                  style={S.navSlash}
                >
                  /
                </span>

                <Link
                  to="/terms"
                  style={
                    S.otherLegalLink
                  }
                >
                  Terms & Conditions
                </Link>

                <span
                  style={S.navSlash}
                >
                  /
                </span>

                <span
                  style={
                    S.currentLegalLink
                  }
                >
                  Privacy Policy
                </span>
              </>
            )}
          </nav>

       

          <div
            style={S.legalHeading}
          >
            Legal
          </div>
        </header>

        {/* =================================
            FIXED SECTION NAV
        ================================= */}
        {showSideNav && (
          <aside style={S.sideNav}>
            <nav
              style={S.sideItems}
              aria-label="Privacy sections"
            >
              {pageSections.map(
                (section) => {
                  const active =
                    activeSection ===
                    section.id;

                  return (
                    <button
                      key={
                        section.id
                      }
                      type="button"
                      onClick={() =>
                        goToSection(
                          section.id
                        )
                      }
                      style={{
                        ...S.sideItem,

                        color: active
                          ? '#111'
                          : '#b4b7bc',

                        fontWeight:
                          active
                            ? 700
                            : 400,

                        opacity:
                          active
                            ? 1
                            : 0.82,
                      }}
                    >
                      {
                        section.label
                      }
                    </button>
                  );
                }
              )}
            </nav>
          </aside>
        )}

        {/* =================================
            DOCUMENT
        ================================= */}
        <div style={S.layout}>
          {showSideNav && (
            <div
              style={
                S.sidePlaceholder
              }
            />
          )}

          <div style={S.content}>
            <span
              id="overview"
              style={{
                display: 'block',
                scrollMarginTop: 100,
              }}
            />

            <h1 style={S.h1}>
              Privacy Policy
            </h1>

            <p style={S.small}>
              Last updated:{' '}
              {new Date().toLocaleDateString()}
            </p>

            <div>
              {renderPrivacy()}
            </div>

            {/* Extra room so the last short sections
                can scroll into the active position */}
            <div
              style={S.bottomScrollSpace}
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
    </main>
  );
}